import { Badge } from '@/components/ui/Badge'
import { Card } from '@/components/ui/Card'
import { CAHAYA_PILLARS } from '@/content/cahaya'
import { useI18n } from '@/i18n'

/**
 * The seven pillars.
 *
 * Seven items divide evenly into no common column count, so a plain grid always
 * strands the last card against the left edge. A centred flex wrap keeps the
 * final row balanced: 1 column on phones, 2 from `sm`, 3 from `lg`.
 *
 * `showDeliverables` is off on the home page, where the grid is a teaser.
 */
export function CahayaGrid({ showDeliverables = false }: { showDeliverables?: boolean }) {
  const { t, pick } = useI18n()

  return (
    <ul className="flex flex-wrap justify-center gap-4">
      {CAHAYA_PILLARS.map((pillar) => (
        <li
          key={pillar.slug}
          className="w-full sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
        >
          <Card interactive className="flex h-full flex-col">
            <div className="flex items-start justify-between gap-3">
              <span
                aria-hidden="true"
                className="gold-text font-serif text-5xl font-bold leading-none opacity-70"
              >
                {pillar.order}
              </span>
              {pillar.status === 'draft' ? <Badge>{t.cahaya.draftBadge}</Badge> : null}
            </div>

            <p className="eyebrow mt-5">{pick(pillar.ordinal)}</p>
            <h3 className="mt-2 text-xl text-parchment">
              <span className="sr-only">
                {t.cahaya.pillarLabel} {pillar.order}:{' '}
              </span>
              {pick(pillar.title)}
            </h3>
            <p className="mt-2.5 flex-1 text-sm text-muted">{pick(pillar.summary)}</p>

            {showDeliverables ? (
              <div className="mt-5 border-t border-line/70 pt-4">
                <p className="eyebrow mb-3">{t.cahaya.pillarLeadLabel}</p>
                <dl className="space-y-1.5 text-sm">
                  <div className="flex justify-between gap-3">
                    <dt className="text-faint">{t.cahaya.leadPerson}</dt>
                    <dd className="text-right text-muted">
                      {pillar.leadPerson ?? t.cahaya.toBeAnnounced}
                    </dd>
                  </div>
                  <div className="flex justify-between gap-3">
                    <dt className="text-faint">{t.cahaya.leadOrganization}</dt>
                    <dd className="text-right text-muted">
                      {pillar.leadOrganization ?? t.cahaya.toBeAnnounced}
                    </dd>
                  </div>
                </dl>
              </div>
            ) : null}

            {showDeliverables ? (
              <div className="mt-5 border-t border-line/70 pt-4">
                <p className="eyebrow mb-3">{t.cahaya.deliverables}</p>
                <ul className="space-y-2">
                  {pick(pillar.deliverables).map((item) => (
                    <li key={item} className="flex gap-2.5 text-sm text-muted">
                      <span
                        aria-hidden="true"
                        className="mt-2 size-1 shrink-0 rounded-full bg-gold"
                      />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </Card>
        </li>
      ))}
    </ul>
  )
}
